'use client'

import React from 'react'
import ReactMarkdown from 'react-markdown'

import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'

import { MARKDOWN_RENDERER_TEST_IDS } from '@/shared/docs-test-ids.const'

import type { MarkdownComponents } from './MarkdownRenderer.types'

import styles from './InfoBox.module.scss'

interface InfoBoxProps {
  type: 'tip' | 'note' | 'warning'
  content: string
  markdownComponents: MarkdownComponents
}

/**
 * InfoBox component for Tip, Note, and Warning callouts
 * Matches Figma design specifications
 */
export function InfoBox({ type, content, markdownComponents }: InfoBoxProps) {
  const title = type === 'tip' ? 'Tip' : type === 'note' ? 'Note' : 'Important'

  return (
    <div data-testid={MARKDOWN_RENDERER_TEST_IDS.root} className={`${styles.infoBox} ${styles[type]}`}>
      <div className={styles.iconContainer}>
        {type === 'tip' && (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M9 21C9 21.55 9.45 22 10 22H14C14.55 22 15 21.55 15 21V20H9V21ZM12 2C8.14 2 5 5.14 5 9C5 11.38 6.19 13.47 8 14.74V17C8 17.55 8.45 18 9 18H15C15.55 18 16 17.55 16 17V14.74C17.81 13.47 19 11.38 19 9C19 5.14 15.86 2 12 2ZM14.85 13.1L14 13.7V16H10V13.7L9.15 13.1C7.8 12.16 7 10.63 7 9C7 6.24 9.24 4 12 4C14.76 4 17 6.24 17 9C17 10.63 16.2 12.16 14.85 13.1Z"
              fill="currentColor"
            />
          </svg>
        )}
        {type === 'note' && (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM11 7H13V9H11V7ZM11 11H13V17H11V11Z"
              fill="currentColor"
            />
          </svg>
        )}
        {type === 'warning' && (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 21H23L12 2L1 21ZM13 18H11V16H13V18ZM13 14H11V10H13V14Z" fill="currentColor" />
          </svg>
        )}
      </div>
      <div className={styles.contentContainer}>
        <p className={styles.title}>{title}</p>
        <div className={styles.content}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={markdownComponents}>
            {content}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  )
}
